<template>
	<rmp-master-account-access-application-base-step v-if="initialized" :title="title">
		<template v-if="counterpartyType === CounterpartyTypeEnum.LEGAL_ENTITY">
			<rmp-legal-entity-details v-if="counterparty" :details="legalEntity"></rmp-legal-entity-details>
		</template>
		<template v-else>
			<rmp-legal-person-details v-if="counterparty" :details="legalPerson"></rmp-legal-person-details>
		</template>
		
		<v-container class="pa-0 mt-3">
			<v-row no-gutters>
				<v-col cols="12">
					<rmp-digital-signature-dialog @signed="handleSign"
					                              :payload="signingPayload"
					                              v-model="signatureDialog">
						<template v-slot:activator="{ on, attrs }">
							<rmp-btn v-on="on" v-bind="attrs"
							         block
							         :disabled="signed">
								{{ signed ? tc("buttons.digitalSignature.isSigned") : tc("buttons.digitalSignature.default") }}
							</rmp-btn>
						</template>
					</rmp-digital-signature-dialog>
					<rmp-link v-if="!signed" class="mt-2" @click="applicationStore.moveToDocumentsStep"
							  :text="tc('common.signatureIsMissing')"/>
				</v-col>
			</v-row>
		</v-container>
	</rmp-master-account-access-application-base-step>
</template>

<script setup lang="ts">
import RmpBtn from "@rmp/core/components/buttons/RmpBtn.vue";
import RmpLink from "@rmp/core/components/common/RmpLink.vue";
import RmpLegalPersonDetails from "@rmp/core/components/details/RmpLegalPersonDetails.vue";
import RmpLegalEntityDetails from "@rmp/core/components/details/RmpLegalEntityDetails.vue";
import RmpMasterAccountAccessApplicationBaseStep from "@rmp/id/views/masterAccountAccess/steps/RmpMasterAccountAccessApplicationBaseStep.vue";
import RmpDigitalSignatureDialog from "@rmp/core/components/digitalSignature/RmpDigitalSignatureDialog.vue";

import { CounterpartyTypeEnum } from "@rmp/core/types/CounterpartyTypeEnum";

import { cloneDeep } from "lodash";
import { processApiPayload } from "@rmp/core/utils/api";

import { useI18n } from "vue-i18n-composable";
import { useMasterAccountAccessSelectCounterpartyStepStore } from "@rmp/id/stores/masterAccountAccess/steps/selectCounterparty";
import { useMasterAccountAccessContactInformationStepStore } from "@rmp/id/stores/masterAccountAccess/steps/contactInformation";
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { usePage } from "@rmp/core/composables/usePage";
import { useMasterAccountAccessStore } from "@rmp/id/stores/masterAccountAccess";
import { useMasterAccountAccessSignApplicationStepStore } from "@rmp/id/stores/masterAccountAccess/steps/signApplication";
import { ApiEgrulLegalEntity, ApiEgrulEgripEntrepreneur } from "@rmp/api-gateway";

const { tc } = useI18n();

const applicationStore = useMasterAccountAccessStore();

const useSelectCounterpartyStepStore = useMasterAccountAccessSelectCounterpartyStepStore;
const selectCounterpartyStepStore = useSelectCounterpartyStepStore();
let { counterparty, counterpartyType } = storeToRefs(selectCounterpartyStepStore);

const contactInformationStepStore = useMasterAccountAccessContactInformationStepStore();

const useStore = useMasterAccountAccessSignApplicationStepStore;
const store = useStore();
let { encodedDetachedSignatureString, encodedApplicationString, signed } = storeToRefs(store);
const {} = useStore();

const signatureDialog = ref(false);
const legalEntity = computed(() => counterparty.value as ApiEgrulLegalEntity);
const legalPerson = computed(() => counterparty.value as ApiEgrulEgripEntrepreneur);

const title = computed(() => {
	switch (counterpartyType.value) {
		case CounterpartyTypeEnum.LEGAL_ENTITY:
			return tc("titles.masterAccountAccessApplication.legalEntityCounterpartyType");
		case CounterpartyTypeEnum.LEGAL_PERSON:
			return tc("titles.masterAccountAccessApplication.legalPersonCounterpartyType");
		default:
			return "";
	}
});

const signingPayload = computed(() => {
	let counterpartyData = cloneDeep(counterparty.value);
	
	processApiPayload(counterpartyData);
	
	if(counterpartyType.value === CounterpartyTypeEnum.LEGAL_ENTITY) {
		return JSON.stringify({ legalEntity: counterpartyData, requestorEmail: contactInformationStepStore.email });
	} else {
		return JSON.stringify({ entrepreneur: counterpartyData, requestorEmail: contactInformationStepStore.email });
	}
});

const handleSign = async ({ data, signature }: { data: string, signature: string }) => {
	try {
		encodedApplicationString.value = data;
		encodedDetachedSignatureString.value = signature;
	} catch (e) {
		console.error(e);
	}
};

const { initialized } = usePage({ useStore });
</script>
